import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { acceptCall, cancelCall, declineCall, getBlockedSlots, getMentorCalls } from '../../../api/v2/calls';
import AvailabilityPicker from '../availability_picker';
import { failToast } from '../../ui_kit/utilities/fail_toast';

import styles from './index.module.css';

const PENDING_STATUSES = ['pending'];
const UPCOMING_STATUSES = ['accepted', 'in_progress'];
const PAST_STATUSES = ['completed', 'canceled', 'declined', 'expired', 'mentor_unreachable', 'patient_unreachable'];

const PAST_LIMIT = 10;
const UPCOMING_LIMIT = 50;

function getInitials(name) {
  if (!name) return 'PP';
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatCallDateTime(iso, timezone) {
  if (!iso) return null;
  const date = new Date(iso);
  const datePart = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(timezone ? { timeZone: timezone } : {}),
  }).format(date);
  const timePart = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(timezone ? { timeZone: timezone } : {}),
  }).format(date);
  return `${datePart} at ${timePart}`;
}

function formatRecentDateTime(iso, timezone) {
  if (!iso) return null;
  const date = new Date(iso);
  const datePart = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(timezone ? { timeZone: timezone } : {}),
  }).format(date);
  const timePart = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(timezone ? { timeZone: timezone } : {}),
  }).format(date);
  return `${datePart} at ${timePart}`;
}

function formatRelativeFuture(iso) {
  if (!iso) return null;
  const diffMs = new Date(iso) - new Date();
  if (diffMs <= 0) return 'starting now';
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.round(diffMs / 3600000);
  if (hours < 24) return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  const days = Math.round(diffMs / 86400000);
  return `in ${days} ${days === 1 ? 'day' : 'days'}`;
}

function formatRelativePast(iso) {
  if (!iso) return null;
  const diffMs = new Date() - new Date(iso);
  if (diffMs < 60000) return 'just now';
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(diffMs / 3600000);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.round(diffMs / 86400000);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

function formatAcceptanceCountdown(iso) {
  if (!iso) return null;
  const diffMs = new Date(iso) - new Date();
  if (diffMs <= 0) return 'Request expired';
  const minutes = Math.max(1, Math.round(diffMs / 60000));
  if (minutes < 60) return `Respond within ${minutes} min`;
  const hours = Math.round(diffMs / 3600000);
  if (hours < 24) return `Respond within ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  const days = Math.round(diffMs / 86400000);
  return `Respond within ${days} ${days === 1 ? 'day' : 'days'}`;
}

function PAST_STATUS_LABEL(status) {
  switch (status) {
    case 'completed':
      return 'Done';
    case 'canceled':
      return 'Canceled';
    case 'declined':
      return 'Declined';
    case 'expired':
      return 'Expired';
    case 'mentor_unreachable':
    case 'patient_unreachable':
      return 'No answer';
    default:
      return status;
  }
}

function isUpcomingCall(call) {
  return UPCOMING_STATUSES.includes(call.status);
}

function isPendingCall(call) {
  return PENDING_STATUSES.includes(call.status);
}

function sortByScheduledAsc(a, b) {
  const aTime = a.scheduled_at ? new Date(a.scheduled_at).getTime() : Number.MAX_SAFE_INTEGER;
  const bTime = b.scheduled_at ? new Date(b.scheduled_at).getTime() : Number.MAX_SAFE_INTEGER;
  return aTime - bTime;
}

function sortByCreatedDesc(a, b) {
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

// ── Icons ──────────────────────────────────────────────────────────────────

const PhoneIcon = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox='0 0 256 256' fill={color} aria-hidden='true'>
    <path d='M231.88,175.08A56.26,56.26,0,0,1,176,224C96.6,224,32,159.4,32,80A56.26,56.26,0,0,1,80.92,24.12a16,16,0,0,1,16.62,9.52l21.12,47.15,0,.12A16,16,0,0,1,117.39,96c-.18.27-.37.52-.57.77L96,121.45c7.49,15.22,23.41,31,38.83,38.51l24.34-20.71a8.12,8.12,0,0,1,.75-.56,16,16,0,0,1,15.17-1.4l.13.06,47.11,21.11A16,16,0,0,1,231.88,175.08Z' />
  </svg>
);

const CalendarIcon = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox='0 0 16 16' fill='none' aria-hidden='true'>
    <rect x='2' y='3' width='12' height='11' rx='2' stroke={color} strokeWidth='1.4' />
    <path d='M2 7H14' stroke={color} strokeWidth='1.4' strokeLinecap='round' />
    <path d='M5.33 1.5V4' stroke={color} strokeWidth='1.4' strokeLinecap='round' />
    <path d='M10.67 1.5V4' stroke={color} strokeWidth='1.4' strokeLinecap='round' />
  </svg>
);

const CloseIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox='0 0 16 16' fill='none' aria-hidden='true'>
    <path d='M3 3L13 13M13 3L3 13' stroke={color} strokeWidth='1.7' strokeLinecap='round' />
  </svg>
);

const DotIcon = ({ size = 11, color = '#3eb0bb' }) => (
  <svg width={size} height={size} viewBox='0 0 16 16' fill={color} aria-hidden='true'>
    <circle cx='8' cy='8' r='4' />
  </svg>
);

const CheckIcon = ({ size = 11, color = '#3eb0bb' }) => (
  <svg width={size} height={size} viewBox='0 0 16 16' fill='none' aria-hidden='true'>
    <path d='M3.5 8.5L6.5 11.5L12.5 5' stroke={color} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const InfoIcon = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox='0 0 16 16' fill='none' aria-hidden='true'>
    <circle cx='8' cy='8' r='6.5' stroke={color} strokeWidth='1.3' />
    <path d='M8 7V11.5' stroke={color} strokeWidth='1.4' strokeLinecap='round' />
    <circle cx='8' cy='4.8' r='0.85' fill={color} />
  </svg>
);

const BackArrowIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox='0 0 256 256' fill={color} aria-hidden='true'>
    <path d='M168.49,199.51a12,12,0,0,1-17,17l-80-80a12,12,0,0,1,0-17l80-80a12,12,0,0,1,17,17L97,128Z' />
  </svg>
);

// ── Gradient avatar with initials ─────────────────────────────────────────

function GradientAvatar({ name, photo, size = 48 }) {
  const radius = Math.round(size / 2);
  const fontSize = size <= 40 ? '15.2px' : '16.3px';
  const letterSpacing = size <= 40 ? '1.216px' : '1.306px';
  return (
    <div className={styles.avatar} style={{ width: size, height: size, borderRadius: radius }} aria-hidden='true'>
      {photo ? (
        <img src={photo} alt='' draggable='false' />
      ) : (
        <span style={{ fontSize, letterSpacing }}>{getInitials(name)}</span>
      )}
    </div>
  );
}

// ── Cards ─────────────────────────────────────────────────────────────────

function PendingCallCard({
  call,
  viewerTimezone,
  isAccepting,
  isDeclining,
  acceptSelectionOpen,
  onAccept,
  onAcceptSlot,
  onToggleAcceptPicker,
  onCounter,
  onDecline,
}) {
  const name = call.patient_name || 'Your mentee';
  const photo = call.patient_avatar_url;
  const isScheduled = Boolean(call.scheduled);
  const proposedSlots = call.proposed_slots || [];
  const hasMultipleSlots = isScheduled && proposedSlots.length > 1;
  const countdown = formatAcceptanceCountdown(call.acceptance_expires_at);
  const requestedAgo = formatRelativePast(call.created_at);
  const disabled = isAccepting || isDeclining;

  const handleAcceptClick = () => {
    if (disabled) return;
    if (hasMultipleSlots) {
      onToggleAcceptPicker?.(call.uuid);
    } else if (isScheduled && proposedSlots.length === 1) {
      onAcceptSlot?.(call, proposedSlots[0]);
    } else {
      onAccept?.(call);
    }
  };

  return (
    <div className={styles.pending_card}>
      <div className={styles.pending_top}>
        <GradientAvatar name={name} photo={photo} size={48} />
        <div className={styles.pending_text}>
          <p className={styles.pending_title}>{name} wants to talk</p>
          <div className={styles.pending_meta}>
            {countdown && (
              <span className={styles.pending_meta_item}>
                <DotIcon size={9} color='#3eb0bb' />
                {countdown}
              </span>
            )}
            {requestedAgo && (
              <>
                <span className={styles.meta_dot} aria-hidden='true'>
                  ·
                </span>
                <span className={styles.pending_meta_item}>Requested {requestedAgo}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {acceptSelectionOpen && hasMultipleSlots && (
        <div className={styles.slot_picker} role='group' aria-label='Choose a time to accept'>
          <p className={styles.slot_picker_label}>Pick a time to accept</p>
          <div className={styles.slot_picker_options}>
            {proposedSlots.map((slot) => (
              <button
                key={slot.start_at}
                type='button'
                className={styles.slot_picker_btn}
                onClick={() => onAcceptSlot?.(call, slot)}
                disabled={disabled}
              >
                {formatCallDateTime(slot.start_at, viewerTimezone)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={styles.pending_actions}>
        <button
          type='button'
          className={`${styles.btn} ${styles.btn_primary}`}
          onClick={handleAcceptClick}
          disabled={disabled}
        >
          <PhoneIcon size={14} color='#ffffff' />
          {isAccepting ? 'Accepting…' : 'Accept'}
        </button>
        {isScheduled && (
          <button
            type='button'
            className={`${styles.btn} ${styles.btn_secondary}`}
            onClick={() => onCounter?.(call)}
            disabled={disabled}
          >
            <CalendarIcon size={14} color='#3eb0bb' />
            Suggest other times
          </button>
        )}
        <button
          type='button'
          className={styles.btn_decline}
          onClick={() => onDecline?.(call)}
          disabled={disabled}
          aria-label={`Decline call request from ${name}`}
        >
          <span className={styles.btn_decline_icon}>
            <CloseIcon size={16} color='#7f98a8' />
          </span>
          <span className={styles.btn_decline_label}>{isDeclining ? 'Declining…' : 'Close'}</span>
        </button>
      </div>
    </div>
  );
}

function ScheduledCallRow({ call, viewerTimezone, isCanceling, onCancel }) {
  const name = call.patient_name || 'Your mentee';
  const photo = call.patient_avatar_url;
  const scheduledAt = call.scheduled_at;
  const statusLabel = call.status === 'in_progress' ? 'In progress' : 'Scheduled';
  const inProgress = call.status === 'in_progress';

  return (
    <div className={styles.row}>
      <Link
        to={call.conversation_uuid ? `/member/conversations/${call.conversation_uuid}` : '#'}
        className={styles.row_main}
        aria-label={`View conversation with ${name}`}
      >
        <GradientAvatar name={name} photo={photo} size={40} />
        <div className={styles.row_text}>
          <p className={styles.row_name}>{name}</p>
          {scheduledAt && (
            <p className={styles.row_subtext}>
              {formatCallDateTime(scheduledAt, viewerTimezone)}
              {viewerTimezone ? ` · ${viewerTimezone}` : ''}
            </p>
          )}
        </div>
      </Link>
      <span className={styles.status_pill}>
        <DotIcon size={9} color='#3eb0bb' />
        {statusLabel}
      </span>
      {call.cancelable && !inProgress && (
        <button type='button' className={styles.cancel_btn} onClick={() => onCancel?.(call)} disabled={isCanceling}>
          {isCanceling ? 'Canceling…' : 'Cancel'}
        </button>
      )}
    </div>
  );
}

function RecentCallRow({ call, viewerTimezone }) {
  const name = call.patient_name || 'Your mentee';
  const photo = call.patient_avatar_url;
  const when = call.scheduled_at || call.ended_at || call.canceled_at || call.created_at;
  return (
    <div className={styles.row}>
      <Link
        to={call.conversation_uuid ? `/member/conversations/${call.conversation_uuid}` : '#'}
        className={styles.row_main}
        aria-label={`View conversation with ${name}`}
      >
        <GradientAvatar name={name} photo={photo} size={40} />
        <div className={styles.row_text}>
          <p className={styles.row_name}>{name}</p>
          {when && <p className={styles.row_subtext}>{formatRecentDateTime(when, viewerTimezone)}</p>}
        </div>
      </Link>
      <span className={styles.status_pill}>{PAST_STATUS_LABEL(call.status)}</span>
    </div>
  );
}

// ── Section wrappers ──────────────────────────────────────────────────────

function SectionShell({ title, count, tone, children, body }) {
  const headerClass = [styles.section_header, tone === 'pending' ? styles.section_header_pending : '']
    .filter(Boolean)
    .join(' ');
  const badgeClass = [styles.count_badge, tone === 'pending' ? styles.count_badge_warning : '']
    .filter(Boolean)
    .join(' ');
  return (
    <section className={styles.section}>
      <div className={headerClass}>
        <h2 className={styles.section_title}>{title}</h2>
        {count != null && <span className={badgeClass}>{count}</span>}
      </div>
      <div className={body ? styles.section_body_padded : styles.section_body}>{children}</div>
    </section>
  );
}

function EmptySectionRow({ message }) {
  return <p className={styles.empty_message}>{message}</p>;
}

// ── Presentational view ──────────────────────────────────────────────────

export function CallsMenuView({
  pendingCalls = [],
  upcomingCalls = [],
  pastCalls = [],
  loading = false,
  viewerTimezone,
  acceptingCallId,
  decliningCallId,
  cancelingCallId,
  acceptPickerCallId,
  onAccept,
  onAcceptSlot,
  onToggleAcceptPicker,
  onCounter,
  onDecline,
  onCancel,
  onHowCallsWork,
}) {
  const isEmpty = !loading && pendingCalls.length === 0 && upcomingCalls.length === 0 && pastCalls.length === 0;

  return (
    <div className={styles.page}>
      <div className={styles.scroll_area}>
        <div className={styles.container}>
          <Link to='/member/conversations' className={styles.back_link} aria-label='Back to conversations'>
            <BackArrowIcon size={16} color='currentColor' />
            <span>Back</span>
          </Link>
          <div className={styles.header}>
            <div className={styles.header_text}>
              <h1 className={styles.title}>Calls</h1>
              <p className={styles.subtitle}>Accept requests, see what's upcoming, and review recent calls.</p>
            </div>
            {onHowCallsWork && (
              <button type='button' className={styles.how_btn} onClick={onHowCallsWork}>
                <InfoIcon size={14} color='#ffffff' />
                <span>How calls work</span>
              </button>
            )}
          </div>

          <div className={styles.stack}>
            {loading && pendingCalls.length === 0 && upcomingCalls.length === 0 && pastCalls.length === 0 ? (
              <EmptySectionRow message='Loading…' />
            ) : (
              <>
                <SectionShell title='Pending' count={pendingCalls.length} tone='pending' body>
                  {pendingCalls.length === 0 ? (
                    <EmptySectionRow message='No pending requests right now.' />
                  ) : (
                    pendingCalls.map((call) => (
                      <PendingCallCard
                        key={call.uuid}
                        call={call}
                        viewerTimezone={viewerTimezone}
                        isAccepting={acceptingCallId === call.uuid}
                        isDeclining={decliningCallId === call.uuid}
                        acceptSelectionOpen={acceptPickerCallId === call.uuid}
                        onAccept={onAccept}
                        onAcceptSlot={onAcceptSlot}
                        onToggleAcceptPicker={onToggleAcceptPicker}
                        onCounter={onCounter}
                        onDecline={onDecline}
                      />
                    ))
                  )}
                </SectionShell>

                <SectionShell title='Upcoming' count={upcomingCalls.length}>
                  {upcomingCalls.length === 0 ? (
                    <EmptySectionRow message='No upcoming scheduled calls.' />
                  ) : (
                    upcomingCalls.map((call) => (
                      <ScheduledCallRow
                        key={call.uuid}
                        call={call}
                        viewerTimezone={viewerTimezone}
                        isCanceling={cancelingCallId === call.uuid}
                        onCancel={onCancel}
                      />
                    ))
                  )}
                </SectionShell>

                <SectionShell title='Recent' count={pastCalls.length}>
                  {pastCalls.length === 0 ? (
                    <EmptySectionRow message='No recent calls yet.' />
                  ) : (
                    pastCalls.map((call) => (
                      <RecentCallRow key={call.uuid} call={call} viewerTimezone={viewerTimezone} />
                    ))
                  )}
                </SectionShell>

                {isEmpty && <p className={styles.empty_message}>You don't have any calls yet.</p>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Container with data fetching + action wiring ──────────────────────────

export default function CallsMenu() {
  const [allCalls, setAllCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptingCallId, setAcceptingCallId] = useState(null);
  const [decliningCallId, setDecliningCallId] = useState(null);
  const [cancelingCallId, setCancelingCallId] = useState(null);
  const [acceptPickerCallId, setAcceptPickerCallId] = useState(null);
  const [counterContext, setCounterContext] = useState(null);

  const history = useHistory();

  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  const viewerTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return null;
    }
  }, []);

  const replaceCall = useCallback((updated) => {
    if (!updated?.uuid) return;
    setAllCalls((prev) => {
      const existing = prev.find((c) => c.uuid === updated.uuid);
      if (existing) return prev.map((c) => (c.uuid === updated.uuid ? { ...c, ...updated } : c));
      return [updated, ...prev];
    });
  }, []);

  const loadCalls = useCallback(() => {
    setLoading(true);
    Promise.all([
      getMentorCalls({ page: 1, tab: 'upcoming', per_page: UPCOMING_LIMIT }),
      getMentorCalls({ page: 1, tab: 'past', per_page: PAST_LIMIT }),
    ])
      .then(([upcomingResp, pastResp]) => {
        if (!isMounted.current) return;
        const combined = [...(upcomingResp.calls || []), ...(pastResp.calls || [])];
        setAllCalls(combined);
      })
      .catch((err) => failToast(err.message || 'Failed to load calls. Please try again.'))
      .finally(() => {
        if (isMounted.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  const pendingCalls = useMemo(
    () =>
      allCalls.filter(isPendingCall).sort((a, b) => {
        const aExp = a.acceptance_expires_at ? new Date(a.acceptance_expires_at).getTime() : Number.MAX_SAFE_INTEGER;
        const bExp = b.acceptance_expires_at ? new Date(b.acceptance_expires_at).getTime() : Number.MAX_SAFE_INTEGER;
        return aExp - bExp;
      }),
    [allCalls]
  );

  const upcomingCalls = useMemo(() => allCalls.filter(isUpcomingCall).sort(sortByScheduledAsc), [allCalls]);

  const pastCalls = useMemo(
    () =>
      allCalls
        .filter((c) => PAST_STATUSES.includes(c.status))
        .sort(sortByCreatedDesc)
        .slice(0, PAST_LIMIT),
    [allCalls]
  );

  const handleAccept = (call) => {
    if (acceptingCallId) return;
    setAcceptingCallId(call.uuid);
    acceptCall({ callId: call.uuid })
      .then((updated) => replaceCall(updated))
      .catch((err) => failToast(err.message || 'Could not accept. Please try again.'))
      .finally(() => {
        if (isMounted.current) setAcceptingCallId(null);
      });
  };

  const handleAcceptSlot = (call, slot) => {
    if (acceptingCallId) return;
    setAcceptingCallId(call.uuid);
    acceptCall({ callId: call.uuid, scheduledAt: slot.start_at })
      .then((updated) => {
        replaceCall(updated);
        setAcceptPickerCallId(null);
      })
      .catch((err) => failToast(err.message || 'Could not accept. Please try again.'))
      .finally(() => {
        if (isMounted.current) setAcceptingCallId(null);
      });
  };

  const handleToggleAcceptPicker = (uuid) => {
    setAcceptPickerCallId((prev) => (prev === uuid ? null : uuid));
  };

  const handleDecline = (call) => {
    if (decliningCallId) return;
    setDecliningCallId(call.uuid);
    declineCall({ callId: call.uuid })
      .then((updated) => replaceCall(updated))
      .catch((err) => failToast(err.message || 'Could not decline. Please try again.'))
      .finally(() => {
        if (isMounted.current) setDecliningCallId(null);
      });
  };

  const handleCancel = (call) => {
    if (cancelingCallId) return;
    setCancelingCallId(call.uuid);
    cancelCall({ callId: call.uuid })
      .then((updated) => replaceCall(updated))
      .catch((err) => failToast(err.message || 'Could not cancel. Please try again.'))
      .finally(() => {
        if (isMounted.current) setCancelingCallId(null);
      });
  };

  const handleCounter = (call) => {
    if (!call.conversation_uuid) {
      failToast('Cannot propose new times for this call right now.');
      return;
    }
    getBlockedSlots(call.conversation_uuid)
      .then((data) => {
        if (!isMounted.current) return;
        setCounterContext({ call, blockedSlots: data?.blocked_slots || [] });
      })
      .catch(() => {
        if (!isMounted.current) return;
        setCounterContext({ call, blockedSlots: [] });
      });
  };

  const handleCounterCreated = (updated) => {
    setCounterContext(null);
    if (updated) replaceCall(updated);
  };

  const handleCounterClose = () => setCounterContext(null);

  return (
    <>
      <CallsMenuView
        pendingCalls={pendingCalls}
        upcomingCalls={upcomingCalls}
        pastCalls={pastCalls}
        loading={loading}
        viewerTimezone={viewerTimezone}
        acceptingCallId={acceptingCallId}
        decliningCallId={decliningCallId}
        cancelingCallId={cancelingCallId}
        acceptPickerCallId={acceptPickerCallId}
        onAccept={handleAccept}
        onAcceptSlot={handleAcceptSlot}
        onToggleAcceptPicker={handleToggleAcceptPicker}
        onDecline={handleDecline}
        onCancel={handleCancel}
        onCounter={handleCounter}
        onHowCallsWork={() =>
          history.push(`/member/phone_calls/mentor?return_to=${encodeURIComponent('/member/calls')}`)
        }
      />
      {counterContext && (
        <AvailabilityPicker
          conversationId={counterContext.call.conversation_uuid}
          existingCallId={counterContext.call.uuid}
          mentorTimezone={viewerTimezone}
          viewerTimezone={viewerTimezone}
          mentorFirstName={(counterContext.call.patient_name || '').split(' ')[0]}
          otherUserName={counterContext.call.patient_name}
          otherUserPhoto={counterContext.call.patient_avatar_url}
          blockedSlots={counterContext.blockedSlots}
          onClose={handleCounterClose}
          onCallCreated={handleCounterCreated}
        />
      )}
    </>
  );
}
